import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { Avatar } from './Avatar';
import { Icon, type IconName } from './Icon';
import { LangToggle } from './LangToggle';
import { ThemeToggle } from './ThemeToggle';
import { LogoMark } from './LogoMark';
import type { AvatarTone } from '@/types/domain';

export interface ConsoleNavItem {
  section?: string;
  to?: string;
  end?: boolean;
  icon?: IconName;
  label?: string;
  badge?: number;
}

interface Who {
  name: string;
  sub: string;
  initials: string;
  tone: AvatarTone;
}

/**
 * Desktop console shell (sidebar + content) shared by the instructor and
 * admin experiences. Pages render their own <ConsoleTopbar> + body inside.
 */
export function ConsoleShell({
  brand,
  roleLabel,
  items,
  who,
  children,
}: {
  brand: string;
  roleLabel: string;
  items: ConsoleNavItem[];
  who: Who;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  return (
    <div style={{ display: 'flex', minHeight: '100dvh', background: 'var(--paper)' }}>
      <aside
        style={{
          width: 248,
          flex: 'none',
          background: 'var(--card)',
          borderRight: '1px solid var(--line)',
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100dvh',
        }}
      >
        <div style={{ padding: '22px 22px 18px' }}>
          <button onClick={() => navigate('/')} style={{ all: 'unset', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center' }}>
            <LogoMark size={30} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.01em', lineHeight: 1 }}>{brand}</div>
              <div className="u-mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.12em', marginTop: 3 }}>{roleLabel}</div>
            </div>
          </button>
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 12px', flex: 1, overflowY: 'auto' }}>
          {/* Bridge to the learner side — admins/instructors are learners too (not a role switch) */}
          <NavLink to="/learn" className="wp-side" style={{ marginBottom: 2 }}>
            <Icon name="book" size={18} stroke={1.6} />
            <span style={{ flex: 1 }}>My learning</span>
            <Icon name="arrowR" size={15} style={{ color: 'var(--ink-4)' }} />
          </NavLink>
          <div style={{ height: 1, background: 'var(--line)', margin: '6px 10px 4px' }} />
          {items.map((it, i) =>
            it.section ? (
              <div key={`s-${i}`} className="eyebrow" style={{ padding: '16px 10px 7px' }}>{it.section}</div>
            ) : (
              <NavLink key={it.to} to={it.to!} end={it.end} className="wp-side">
                {({ isActive }) => (
                  <>
                    <Icon name={it.icon!} size={18} stroke={isActive ? 2 : 1.6} />
                    <span style={{ flex: 1 }}>{it.label}</span>
                    {it.badge != null && <span className="badge badge-sage" style={{ padding: '2px 7px', fontSize: 11 }}>{it.badge}</span>}
                  </>
                )}
              </NavLink>
            ),
          )}
        </nav>
        <div style={{ padding: 14, borderTop: '1px solid var(--line)', display: 'flex', gap: 10, alignItems: 'center' }}>
          <Avatar initials={who.initials} size={36} tone={who.tone} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{who.name}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{who.sub}</div>
          </div>
          <button
            onClick={() => { signOut(); navigate('/welcome'); }}
            title="Sign out"
            aria-label="Sign out"
            style={{ flex: 'none', border: 'none', background: 'transparent', cursor: 'pointer', padding: 8, borderRadius: 8, color: 'var(--ink-3)' }}
          >
            <Icon name="logout" size={18} />
          </button>
        </div>
      </aside>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>{children}</div>
    </div>
  );
}

export function ConsoleTopbar({
  title,
  th,
  crumbs,
  actions,
}: {
  title: string;
  th?: string;
  crumbs?: string;
  actions?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '18px 28px',
        borderBottom: '1px solid var(--line)',
        background: 'var(--paper)',
        position: 'sticky',
        top: 0,
        zIndex: 20,
      }}
    >
      <div>
        {crumbs && <div className="u-mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '.08em', marginBottom: 5 }}>{crumbs}</div>}
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-.02em' }}>{title}</h1>
          {th && <span className="th" style={{ fontSize: 14, color: 'var(--ink-3)' }}>{th}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {actions}
        <ThemeToggle />
        <LangToggle />
      </div>
    </div>
  );
}
